import { makeAutoObservable, runInAction } from 'mobx'
import {
	fetchCurriculumProgress,
	markCurriculumSessionComplete,
} from 'api/curriculum'
import {
	CURRICULUM_BY_GRADE,
	GRADE_CURRICULUM_META,
	DEFAULT_GRADE_ID,
} from '../config/curriculumConfig'
import { classroomStore } from 'pages/Classroom/model/classroomStore'
import { studentsStore } from 'pages/Students/model/studentsStore'

const USE_CURRICULUM_API = process.env.REACT_APP_CURRICULUM_API !== '0'

export const progressKey = (grade, sessionId) => `${grade}:${sessionId}`

const normalizeType = value =>
	String(value || '')
		.trim()
		.toLowerCase()

const getGradeKeyFromStudentGrade = grade => {
	if (grade === undefined || grade === null) return null
	const str = String(grade).trim()
	if (!str) return null
	const match = str.match(/\d+/)
	if (!match) return null
	const n = parseInt(match[0], 10)
	if (!Number.isFinite(n) || n < 1 || n > 20) return null
	return String(n)
}

class CurriculumStore {
	viewMode = 'table'
	activeGradeId = DEFAULT_GRADE_ID
	progressByKey = new Map()
	loadStatus = 'idle'
	hydratedOnce = false
	classroomActions = null

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true })
	}

	setViewMode(mode) {
		if (mode !== 'table' && mode !== 'curriculum') return
		this.viewMode = mode
	}

	setActiveGradeId(gradeId) {
		const exists = CURRICULUM_BY_GRADE.some(g => g.id === gradeId)
		if (exists) this.activeGradeId = gradeId
	}

	syncGradeFromClassroomTab(tabId) {
		if (!tabId || tabId === 'all') return
		const match = String(tabId).match(/^grade_(\d+)$/)
		if (match) this.activeGradeId = `grade-${match[1]}`
	}

	resolveDefaultGradeId() {
		return studentsStore.firstConsultantCurriculumGradeId || DEFAULT_GRADE_ID
	}

	syncDefaultGradeFromStudentsIfNeeded() {
		const firstId = studentsStore.firstConsultantCurriculumGradeId
		if (!firstId || this.activeGradeId !== DEFAULT_GRADE_ID) return this.activeGradeId
		this.setActiveGradeId(firstId)
		return firstId
	}

	applyDefaultGradeForCurriculumView() {
		const gradeId = this.resolveDefaultGradeId()
		this.setActiveGradeId(gradeId)
		return gradeId
	}

	classroomTabFromGradeId(gradeId) {
		const match = String(gradeId || '').match(/^grade-(\d+)$/)
		return match ? `grade_${match[1]}` : 'grade_9'
	}

	gradeFromGradeId(gradeId) {
		const match = String(gradeId || '').match(/^grade-(\d+)$/)
		return match ? Number(match[1]) : null
	}

	setLoadStatus(status) {
		this.loadStatus = status
	}

	bindClassroomActions(actions) {
		this.classroomActions = actions
	}

	getProgressEntry(grade, sessionId) {
		return this.progressByKey.get(progressKey(grade, sessionId)) || null
	}

	isSessionCompleted(session, grade = this.activeGrade?.grade) {
		if (!session?.id || grade == null) return false
		return this.progressByKey.has(progressKey(grade, session.id))
	}

	findFirstIncompleteSessionIdForGrade(gradeConfig) {
		const grade = gradeConfig?.grade
		if (grade == null) return null
		const sessions = Array.isArray(gradeConfig.sessions)
			? gradeConfig.sessions
			: []
		const pending = sessions.find(s => !this.isSessionCompleted(s, grade))
		return pending?.id || null
	}

	get currentSessionId() {
		const grade = this.activeGrade?.grade
		if (grade == null) return null
		return this.findFirstIncompleteSessionIdForGrade(this.activeGrade)
	}

	resolveAssignmentsForSession(grade, assignmentType) {
		if (grade == null || !assignmentType) return []

		const targetGrade = String(grade)
		const targetType = normalizeType(assignmentType)

		return (classroomStore.allAssignments || []).filter(assignment => {
			if (normalizeType(assignment.type) !== targetType) return false
			const students = assignment?.students || []
			return students.some(
				s => getGradeKeyFromStudentGrade(s.grade) === targetGrade,
			)
		})
	}

	getAssignmentButtonState(session, grade = this.activeGrade?.grade) {
		const matches = this.resolveAssignmentsForSession(
			grade,
			session?.assignmentType,
		)
		const count = matches.length

		if (count === 0) {
			return {
				case: 'none',
				label: 'Create Assignment',
				matchCount: 0,
				taskId: undefined,
				matches: [],
			}
		}

		if (count === 1) {
			return {
				case: 'single',
				label: 'Open Assignment',
				matchCount: 1,
				taskId: matches[0]?.id,
				matches,
			}
		}

		return {
			case: 'multiple',
			label: 'Open Assignment',
			matchCount: count,
			taskId: undefined,
			matches,
		}
	}

	applyProgressRows(rows) {
		const next = new Map()
		const list = Array.isArray(rows) ? rows : []
		list.forEach(row => {
			const grade = Number(row?.grade)
			const sessionId = String(row?.sessionId || '').trim()
			const completedAt = row?.completedAt || null
			if (!sessionId || !Number.isFinite(grade) || !completedAt) return
			next.set(progressKey(grade, sessionId), {
				grade,
				sessionId,
				completedAt,
			})
		})
		this.progressByKey = next
	}

	async loadProgress({ grade, force = false } = {}) {
		if (!USE_CURRICULUM_API) {
			this.progressByKey = new Map()
			this.loadStatus = 'ready'
			this.hydratedOnce = true
			return
		}

		if (!force && this.hydratedOnce && this.loadStatus === 'ready') {
			return
		}

		this.setLoadStatus('loading')
		try {
			const data = await fetchCurriculumProgress({ grade })
			runInAction(() => {
				this.applyProgressRows(data?.progress)
				this.loadStatus = 'ready'
				this.hydratedOnce = true
			})
		} catch (error) {
			runInAction(() => {
				this.loadStatus = 'error'
			})
			throw error
		}
	}

	async markSessionComplete({ grade, sessionId }) {
		const resolvedGrade =
			grade != null ? Number(grade) : this.gradeFromGradeId(this.activeGradeId)
		const key = progressKey(resolvedGrade, sessionId)
		const previous = this.getProgressEntry(resolvedGrade, sessionId)
		const optimisticAt = new Date().toISOString()

		this.progressByKey.set(key, {
			grade: resolvedGrade,
			sessionId,
			completedAt: optimisticAt,
		})

		if (!USE_CURRICULUM_API) {
			return this.getProgressEntry(resolvedGrade, sessionId)
		}

		try {
			const data = await markCurriculumSessionComplete({
				sessionId,
				grade: resolvedGrade,
			})
			runInAction(() => {
				this.progressByKey.set(key, {
					grade: data?.grade ?? resolvedGrade,
					sessionId: data?.sessionId ?? sessionId,
					completedAt: data?.completedAt ?? optimisticAt,
				})
			})
			return this.getProgressEntry(resolvedGrade, sessionId)
		} catch (error) {
			runInAction(() => {
				if (previous) {
					this.progressByKey.set(key, previous)
				} else {
					this.progressByKey.delete(key)
				}
			})
			throw error
		}
	}

	handleAssignmentClick(session) {
		const grade = this.activeGrade?.grade
		if (grade == null || !session) return

		const button = this.getAssignmentButtonState(session, grade)
		const actions = this.classroomActions
		if (!actions) return

		if (button.case === 'none') {
			actions.openCreateModal?.({
				grade,
				assignmentType: session.assignmentType || '',
				title: session.title || '',
			})
			return
		}

		if (button.case === 'single' && button.matches[0]) {
			actions.openAssignmentDetails?.(button.matches[0])
			return
		}

		if (button.case === 'multiple') {
			actions.openAssignmentsTable?.({
				grade,
				assignmentType: session.assignmentType || '',
			})
		}
	}

	handleResourceClick(session, resourceKey) {
		const url = session?.resources?.[resourceKey]
		if (url) {
			window.open(url, '_blank', 'noopener,noreferrer')
		}
	}

	get curriculumByGrade() {
		return CURRICULUM_BY_GRADE
	}

	get activeGrade() {
		return (
			CURRICULUM_BY_GRADE.find(g => g.id === this.activeGradeId) ||
			CURRICULUM_BY_GRADE[0] ||
			null
		)
	}

	get activeGradeMeta() {
		const grade = this.activeGrade?.grade
		if (!grade) return { title: '', description: '' }
		const meta = GRADE_CURRICULUM_META[grade] || {}
		const gradeConfig = this.activeGrade
		return {
			title: gradeConfig?.title || meta.title || '',
			description: meta.description || '',
		}
	}

	getGradeProgressStats(gradeConfig) {
		const grade = gradeConfig?.grade
		const sessions = Array.isArray(gradeConfig?.sessions)
			? gradeConfig.sessions
			: []
		const total = sessions.length
		const completed = sessions.filter(session =>
			this.isSessionCompleted(session, grade),
		).length
		const percent = total ? Math.round((completed / total) * 100) : 0

		return { completed, total, percent }
	}

	getGradeStatus(gradeConfig) {
		const { completed, total } = this.getGradeProgressStats(gradeConfig)
		if (total > 0 && completed === total) return 'completed'
		return 'active'
	}

	getDisplayedSessionForGrade(gradeConfig) {
		const grade = gradeConfig?.grade
		const sessions = Array.isArray(gradeConfig?.sessions)
			? gradeConfig.sessions
			: []
		if (!sessions.length || grade == null) return null

		const allComplete = sessions.every(session =>
			this.isSessionCompleted(session, grade),
		)
		if (allComplete) {
			return sessions[sessions.length - 1]
		}

		const currentSessionId = this.findFirstIncompleteSessionIdForGrade(gradeConfig)
		return sessions.find(session => session.id === currentSessionId) || sessions[0]
	}

	enrichSessionForGrade(session, gradeConfig) {
		const grade = gradeConfig?.grade
		const sessions = Array.isArray(gradeConfig?.sessions)
			? gradeConfig.sessions
			: []
		const index = sessions.findIndex(item => item.id === session?.id)
		const currentSessionId = this.findFirstIncompleteSessionIdForGrade(gradeConfig)
		const completedAt =
			this.getProgressEntry(grade, session.id)?.completedAt || null
		const assignmentButton = this.getAssignmentButtonState(session, grade)

		return {
			...session,
			grade,
			subtitle: session.subtitle || `Grade ${grade} · Core Curriculum`,
			completed: Boolean(completedAt),
			completedAt,
			assignmentButton,
			status: this.getSessionStatus(session, index, sessions, grade, {
				currentSessionId,
			}),
		}
	}

	get dashboardGradeCards() {
		const consultantGrades = new Set(studentsStore.consultantCurriculumGrades)
		if (!consultantGrades.size) return []

		return CURRICULUM_BY_GRADE.filter(gradeConfig =>
			consultantGrades.has(gradeConfig.grade),
		)
			.map(gradeConfig => {
				const session = this.getDisplayedSessionForGrade(gradeConfig)
				if (!session) return null

				const progress = this.getGradeProgressStats(gradeConfig)
				const gradeStatus = this.getGradeStatus(gradeConfig)

				return {
					gradeId: gradeConfig.id,
					grade: gradeConfig.grade,
					gradeTab: this.classroomTabFromGradeId(gradeConfig.id),
					gradeStatus,
					progress,
					session: this.enrichSessionForGrade(session, gradeConfig),
				}
			})
			.filter(Boolean)
	}

	get sessionsForActiveGrade() {
		const grade = this.activeGrade?.grade
		const sessions = Array.isArray(this.activeGrade?.sessions)
			? this.activeGrade.sessions
			: []

		return sessions.map((session, index) => {
			const completedAt =
				this.getProgressEntry(grade, session.id)?.completedAt || null
			const assignmentButton = this.getAssignmentButtonState(session, grade)

			return {
				...session,
				grade,
				subtitle: session.subtitle || `Grade ${grade} · Core Curriculum`,
				completed: Boolean(completedAt),
				completedAt,
				assignmentButton,
				status: this.getSessionStatus(session, index, sessions, grade),
			}
		})
	}

	getSessionStatus(
		session,
		index,
		sessions,
		grade = this.activeGrade?.grade,
		{ currentSessionId } = {},
	) {
		const resolvedCurrentSessionId =
			currentSessionId ??
			this.findFirstIncompleteSessionIdForGrade({ grade, sessions })
		const isCompleted = this.isSessionCompleted(session, grade)
		const isCurrent = resolvedCurrentSessionId === session.id
		const assignmentButton = this.getAssignmentButtonState(session, grade)
		const hasAssignment = assignmentButton.case !== 'none'
		const openLabel = assignmentButton.label
		const configureLabel = 'Create Assignment'

		if (isCompleted) {
			return {
				primary: 'done',
				secondary: hasAssignment ? 'configured' : 'not_configured',
				action: hasAssignment ? 'open' : 'configure',
				actionLabel: hasAssignment ? openLabel : configureLabel,
			}
		}

		if (isCurrent) {
			return {
				primary: 'active',
				secondary: hasAssignment ? 'configured' : 'not_configured',
				action: hasAssignment ? 'open' : 'configure',
				actionLabel: hasAssignment ? openLabel : configureLabel,
			}
		}

		const firstIncompleteIndex = sessions.findIndex(
			s => !this.isSessionCompleted(s, grade),
		)

		if (index === firstIncompleteIndex) {
			return {
				primary: 'next',
				secondary: hasAssignment ? 'configured' : 'not_configured',
				action: hasAssignment ? 'open' : 'configure',
				actionLabel: hasAssignment ? openLabel : configureLabel,
			}
		}

		return {
			primary: 'not_run',
			secondary: hasAssignment ? 'configured' : 'not_configured',
			action: hasAssignment ? 'open' : 'configure',
			actionLabel: hasAssignment ? openLabel : configureLabel,
		}
	}
}

export const curriculumStore = new CurriculumStore()
